<template>
  <v-container fluid>
    <v-row class="pb-3" align="center">
      <v-col cols="12" md="auto">
        <v-radio-group
          v-model="selectedSupplierType"
          row
          class="ma-0 py-0"
          dense
          hide-details
        >
          <v-radio
            v-for="sType in supplierTypes"
            :key="sType"
            :value="sType"
            dense
            hide-details
          >
            <template v-slot:label>
              <v-icon left>{{ supplierTypeIcon(sType) }}</v-icon>
            </template>
          </v-radio>
        </v-radio-group>
      </v-col>
      <v-col cols="12" md="4">
        <gt-multi-select
          :value="selectedSupplierIds"
          :items="suppliersList"
          item-text="name"
          item-value="id"
          label="Filter by Supplier"
          dense
          outlined
          @input="selectedSupplierIds = $event"
        />
      </v-col>
      <v-col cols="12" md="4">
        <gt-multi-select
          :value="selectedServiceIds"
          :items="servicesList"
          item-text="name"
          item-value="id"
          label="Filter by Service"
          dense
          outlined
          @input="selectedServiceIds = $event"
        />
      </v-col>
    </v-row>
    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :expanded.sync="expanded"
      show-expand
      item-key="id"
      class="elevation-1"
    >
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-4">
          <v-row>
            <v-col cols="12">
              <h4>Assigned Participants</h4>
              <v-chip-group column multiple>
                <v-chip
                  v-for="participant in getAssignedParticipants(item.id)"
                  :key="participant.id"
                  close
                  @click:close="removeParticipant(item.id, participant.id)"
                >
                  {{ participant.name }} {{ participant.surname }}
                </v-chip>
              </v-chip-group>
              <v-divider class="my-2"></v-divider>
              <h4>Select Participants</h4>
              <gt-multi-select
                :value="getAssignedParticipantIds(item.id)"
                :items="participantOptions"
                item-text="fullname"
                item-value="id"
                label="Select Participants"
                dense
                outlined
                @input="
                  (selectedIds) =>
                    updateParticipantAssignments(item.id, selectedIds)
                "
              />
            </v-col>
          </v-row>
        </td>
      </template>
      <template v-slot:item.startDate="{ item }">
        {{ item.startDate | formatDate }}
      </template>
      <template v-slot:item.pax="{ item }">
        {{ item.pax.adults || 0 }}/{{ item.pax.children || 0 }}/{{
          item.pax.infants || 0
        }}
      </template>
      <template v-slot:item.assigned="{ item }">
        {{ getAssignedCount(item.id) }}
      </template>
      <template v-slot:item.assignedParticipants="{ item }">
        <v-chip
          v-for="participant in getAssignedParticipants(item.id)"
          :key="participant.id"
          small
          class="mr-1"
        >
          {{ participant.name }} {{ participant.surname }}
        </v-chip>
      </template>
      <template v-slot:item.remaining="{ item }">
        {{ getRemainingCount(item) }}
      </template>
    </v-data-table>
    <v-row v-if="debug">
      <code>
        <pre>
       suppliers: {{ suppliersList }}
       services: {{ servicesList }}
      </pre
        >
      </code>
    </v-row>
  </v-container>
</template>

<script>
import { SUPPLIER_TYPES } from "../../../../constants/supplierTypes";
import { SupplierTypeIconMixin } from "../../../../mixins/SupplierTypeIconMixin";
import GtMultiSelect from "../../shared/GtMultiSelect.vue";

export default {
  name: "RoomingListByRoom",
  mixins: [SupplierTypeIconMixin],
  components: {
    GtMultiSelect,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    participants: {
      type: Array,
      required: true,
    },
    assignments: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      debug: false,
      expanded: [],
      selectedSupplierType: "ACCOMMODATION",
      selectedSupplierIds: [],
      selectedServiceIds: [],
      supplierTypes: SUPPLIER_TYPES,
      headers: [
        { text: "Date", value: "startDate" },
        { text: "Supplier", value: "supplierName" },
        { text: "Service", value: "serviceName" },
        { text: "Ad/Ch/Inf", value: "pax" },
        { text: "Assigned", value: "assigned" },
        { text: "Assigned Participants", value: "assignedParticipants" },
        { text: "Remaining", value: "remaining" },
        { text: "", value: "data-table-expand" },
      ],
    };
  },
  computed: {
    filteredItems() {
      let filtered = this.items;

      // Filter by supplier type
      if (this.selectedSupplierType) {
        filtered = filtered.filter(
          (item) => item.serviceTypeName === this.selectedSupplierType
        );
      }

      // Filter by supplier IDs
      if (this.selectedSupplierIds && this.selectedSupplierIds.length > 0) {
        filtered = filtered.filter(
          (item) =>
            this.selectedSupplierIds.includes(item.supplierId) ||
            (item.supplier &&
              this.selectedSupplierIds.includes(item.supplier.id))
        );
      }

      // Filter by service IDs
      if (this.selectedServiceIds && this.selectedServiceIds.length > 0) {
        filtered = filtered.filter(
          (item) =>
            this.selectedServiceIds.includes(item.serviceId) ||
            (item.service && this.selectedServiceIds.includes(item.service.id))
        );
      }

      return filtered;
    },
    participantOptions() {
      return this.participants.map((p) => ({
        ...p,
        fullname: `${p.name} ${p.surname}`,
      }));
    },
    suppliersList() {
      // Filter by supplier type if selected
      let filteredItems = this.items;
      if (this.selectedSupplierType) {
        filteredItems = filteredItems.filter(
          (item) => item.serviceTypeName === this.selectedSupplierType
        );
      }

      const uniqueSuppliers = new Set(
        filteredItems.map((item) => item.supplierId)
      );
      const suppliers = filteredItems
        .filter((item) => uniqueSuppliers.has(item.supplierId))
        .map((item) => ({
          name: item.supplierName,
          id: item.supplier.id,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      return suppliers;
    },
    servicesList() {
      // Filter by supplier type if selected
      let filteredItems = this.items;
      if (this.selectedSupplierType) {
        filteredItems = filteredItems.filter(
          (item) => item.serviceTypeName === this.selectedSupplierType
        );
      }

      const uniqueServices = new Set(
        filteredItems.map((item) => item.serviceId)
      );
      const services = filteredItems
        .filter((item) => uniqueServices.has(item.serviceId))
        .map((item) => ({
          name: item.serviceName,
          id: item.service.id,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      return services;
    },
  },
  methods: {
    getAssignedParticipants(serviceId) {
      // Find participants that have this serviceId in their assignments array
      return this.participants.filter((p) => {
        const participantServices = this.assignments[p.id] || [];
        return participantServices.includes(serviceId);
      });
    },
    availableParticipants(serviceId) {
      // Filter out participants who already have this serviceId assigned
      return this.participantOptions.filter((p) => {
        const participantServices = this.assignments[p.id] || [];
        return !participantServices.includes(serviceId);
      });
    },
    getAssignedCount(serviceId) {
      // Count participants that have this serviceId in their assignments
      return this.participants.filter((p) => {
        const participantServices = this.assignments[p.id] || [];
        return participantServices.includes(serviceId);
      }).length;
    },
    getRemainingCount(item) {
      const totalPax =
        (item.pax.adults || 0) +
        (item.pax.children || 0) +
        (item.pax.infants || 0);
      return totalPax - this.getAssignedCount(item.id);
    },
    getAssignedParticipantIds(serviceId) {
      // Get array of participant IDs that have this serviceId assigned
      return this.participants
        .filter((p) => {
          const participantServices = this.assignments[p.id] || [];
          return participantServices.includes(serviceId);
        })
        .map((p) => p.id);
    },
    updateParticipantAssignments(serviceId, selectedParticipantIds) {
      // Update assignments: add serviceId to newly selected participants,
      // remove serviceId from deselected participants
      const newAssignments = { ...this.assignments };
      const currentAssignedIds = this.getAssignedParticipantIds(serviceId);

      // Find participants that were added (in new selection but not in old)
      const addedIds = selectedParticipantIds.filter(
        (id) => !currentAssignedIds.includes(id)
      );
      // Find participants that were removed (in old selection but not in new)
      const removedIds = currentAssignedIds.filter(
        (id) => !selectedParticipantIds.includes(id)
      );

      // Add serviceId to newly selected participants
      addedIds.forEach((participantId) => {
        const currentServices = newAssignments[participantId] || [];
        if (!currentServices.includes(serviceId)) {
          newAssignments[participantId] = [...currentServices, serviceId];
        }
      });

      // Remove serviceId from deselected participants
      removedIds.forEach((participantId) => {
        const currentServices = newAssignments[participantId] || [];
        newAssignments[participantId] = currentServices.filter(
          (id) => id !== serviceId
        );
      });

      this.$emit("update:assignments", newAssignments);
    },
    addParticipant(serviceId, participant) {
      if (!participant) return;
      // Add serviceId to participant's assignments array
      const currentServices = this.assignments[participant.id] || [];
      if (!currentServices.includes(serviceId)) {
        const newAssignments = {
          ...this.assignments,
          [participant.id]: [...currentServices, serviceId],
        };
        this.$emit("update:assignments", newAssignments);
      }
    },
    removeParticipant(serviceId, participantId) {
      // Remove serviceId from participant's assignments array
      const currentServices = this.assignments[participantId] || [];
      const newAssignments = {
        ...this.assignments,
        [participantId]: currentServices.filter((id) => id !== serviceId),
      };
      this.$emit("update:assignments", newAssignments);
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/rooming-list/RoomingListByRoom.vue